import type { CityConfig } from "../city-template/types";

export const laconiaConfig: CityConfig = {
  city: "Laconia",
  state: "New Hampshire",
  stateAbbr: "NH",
  slug: "laconia",
  route: "/cities/laconia",
  title: "Emergency Passport Laconia, NH | Fast Passport Renewal & Expedited Help",
  metaDescription: "Emergency passport and expedited passport services in Laconia, NH — as fast as 24 hours. Fast passport renewal, same-day help, and child passport expediting for Lakes Region and Belknap County travelers. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Laconia, NH",
  heroSubtitle: "Passport expired before your trip? Need an emergency passport fast? We help Laconia and Lakes Region travelers get their expedited passports as quickly as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Laconia%20New%20Hampshire%20Lakes%20Region%20view%20with%20Lake%20Winnipesaukee%20and%20the%20Weirs%20Beach%20boardwalk%2C%20beautiful%20summer%20waterfront%20scene%20with%20boats%20and%20clear%20blue%20water%2C%20Mount%20Major%20in%20the%20background%2C%20warm%20New%20England%20light%2C%20vibrant%20and%20clean%20inviting%20landscape%2C%20professional%20nature%20photography%2C%20wide%20panoramic%20view%2C%20no%20visible%20text%20or%20people%2C%20soft%20blue%20sky%20and%20lush%20green%20hills%2C%20photorealistic%20scenery&width=1800&height=700&seq=laconia-nh-hero-001&orientation=landscape",
  heroImageAlt: "Laconia New Hampshire Lakes Region view with Lake Winnipesaukee, Weirs Beach boardwalk, and Mount Major in the background",
  geoPosition: "43.5279;-71.4704",
  geoPlacename: "Laconia, New Hampshire",
  regionalAgency: {
    name: "Boston Passport Agency",
    address: "10 Causeway St Suite 247, Boston, MA 02222",
    distance: "Approximately 100–120 minutes, 100 miles from Laconia via I-93",
  },
  nearbyCitiesZips: [
    { name: "Laconia", zips: "03246" },
    { name: "Gilford", zips: "03249" },
    { name: "Belmont", zips: "03220" },
    { name: "Meredith", zips: "03253" },
    { name: "Alton", zips: "03809" },
    { name: "Center Harbor", zips: "03226" },
    { name: "Tilton", zips: "03276" },
    { name: "Franklin", zips: "03235" },
    { name: "Wolfeboro", zips: "03894" },
    { name: "Tuftonboro", zips: "03816" },
    { name: "Moultonborough", zips: "03254" },
    { name: "Ossipee", zips: "03864" },
    { name: "Plymouth", zips: "03264" },
    { name: "Ashland", zips: "03217" },
    { name: "Holderness", zips: "03245" },
    { name: "Sanbornton", zips: "03269" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent Lakes Region departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels to our processing center" },
  ],
  internalLinks: [
    { name: "Manchester", route: "/manchester" },
    { name: "Nashua", route: "/cities/nashua" },
    { name: "Concord", route: "/concord-nh" },
    { name: "Portsmouth", route: "/cities/portsmouth" },
    { name: "Keene", route: "/cities/keene" },
    { name: "Dover", route: "/cities/dover" },
    { name: "Rochester", route: "/cities/rochester" },
    { name: "Lebanon", route: "/cities/lebanon" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Laconia remotely via secure courier. We do not have a walk-in office in Laconia — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for Lakes Region travelers.",
  fpcOfficeAddress: "1 New Hampshire Ave, Portsmouth, NH 03801",
  fpcOfficeDistance: "Serving all of Belknap and Carroll Counties",
  serviceAreaIntro: "From Laconia's Weirs Beach to the downtown Main Street district, from the Gilford border to the Meredith line — Fast Passport Center serves the entire Laconia community. We regularly help travelers from Gilford, Belmont, Meredith, and Alton get their passports fast.",
  nearbyCitiesIntro: "We serve all Laconia neighborhoods and nearby Lakes Region cities including Gilford, Belmont, Meredith, Alton, Center Harbor, Tilton, Franklin, Wolfeboro, Tuftonboro, Moultonborough, Ossipee, Plymouth, Ashland, Holderness, and Sanbornton. If you are in the greater Laconia area, we have you covered.",
  howItWorksIntro: "Most Laconia travelers complete everything online and ship documents overnight. No need to drive to Boston or wait in line at the regional agency.",
  mistakesIntro: "With Laconia Municipal Airport (LCI) and Manchester-Boston Regional (MHT) about an hour away, Lakes Region travelers can't afford passport delays. Here are the most common mistakes we see.",
  testimonialsIntro: "Laconia and Lakes Region travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Laconia? Here are the most common ones we hear from Lakes Region travelers.",
  processingIntro: "Laconia travelers — seasonal hospitality workers heading to international resorts, Lakes Region retirees maintaining active European travel schedules, Motorcycle Week visitors who discover passport issues before international rally trips, and families booking last-minute cruises from Boston — have several options for getting a passport fast. Here is how they compare, and why most Belknap County residents choose a U.S. Department of State registered passport courier for predictable, professional processing without driving to Boston.",
  localAreaIntro: [
    "Laconia, New Hampshire is the beating heart of the Lakes Region — a tourism-driven economy anchored by Lake Winnipesaukee, Lake Winnisquam, and the Weirs Beach boardwalk. The city's identity is inseparable from seasonal tourism: the annual Motorcycle Week draws over 200,000 visitors, summer lakefront rentals host families from across New England, and winter sports at Gunstock Mountain Resort in neighboring Gilford attract an international crowd. This tourism economy creates a unique passport demand profile. Seasonal workers — lifeguards, marina staff, hotel managers, and restaurant professionals — frequently transition between summer seasons in New Hampshire and winter seasons at resorts in the Caribbean, the Alps, or South America. When a seasonal worker discovers their passport expired during the off-season transition, they need a solution that matches the hospitality industry's tight turnaround expectations.",
    "The Lakes Region is also home to a substantial retiree population — empty-nesters and second-home owners who chose Laconia, Meredith, and Gilford for the quality of life and now maintain active international travel schedules. River cruises on the Danube, villa rentals in Tuscany, and winter escapes to Costa Rica are common among this demographic, and they often plan travel around fixed social commitments — a grandchild's destination wedding, a college reunion cruise, a group tour with a departure date that cannot move. Retirees in the Lakes Region frequently let their passports lapse between trips, especially during the pandemic-era travel lull, and discover the issue only when booking their next adventure. Standard processing timelines of 6—13 weeks simply do not align with their expectations of responsive, professional service.",
    "Laconia's central location in the Lakes Region makes it a travel hub for the broader Belknap and Carroll County area. Laconia Municipal Airport (LCI) provides general aviation and seasonal commercial service, while Manchester-Boston Regional Airport (MHT) is just 45 miles south with direct international routes. The region's hotels and inns serve as staging points for international visitors arriving to explore New Hampshire — and those same property owners and hospitality professionals travel abroad themselves during the shoulder seasons. The tourism cycle means passport demand peaks in April—May (before summer staffing begins) and September—October (as seasonal workers prepare for winter positions abroad), creating a rhythm that the standard passport processing system is not designed to accommodate.",
    "Family travel rounds out the Laconia passport picture. The Lakes Region is a generational destination — families who visited as children now bring their own kids to the same lakefront cabins — and that family tradition increasingly extends to international travel. Multi-generational trips to Quebec, cruises from Boston to Bermuda, and winter breaks in the Dominican Republic are common. Parents often discover at the last minute that children under 16 need their own passports, that both parents must appear in person for a child's first passport, or that a teenager's five-year passport has expired. The Laconia City Clerk and local USPS acceptance facilities in Gilford, Belmont, and Meredith provide the required in-person verification, but Fast Passport Center's courier service ensures the documents actually get processed on a family's timeline."
  ],
  statePageRoute: "/states/new-hampshire",
  statePageName: "New Hampshire",
  dmvWebsite: "https://www.nh.gov/safety/divisions/dmv",
  dmvLabel: "New Hampshire DMV — Official Website",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: ["Appointment required — no walk-ins allowed", "Life-or-death emergencies only", "Bereavement or medical crisis", "Expect to wait all day or return next day"],
      highlight: false,
      emergency: true,
    },
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Predictable turnaround", "Ideal for upcoming trips", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Faster than self-service", "Good for planned travel", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: ["No courier access — standard mail only", "Workload & wait times shift with shutdowns and budget fights", "In-person clerk appointment required", "No document review — errors cause rejection", "No dedicated agent — you are on your own", "Updates only by physical mail letter", "No phone or email contact for your case", "Can't upgrade speed after submission", "Not suitable if travel is within 10 weeks"],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For travelers with immediate departure needs — as fast as 24 hours for urgent Laconia international flights.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance and police report documentation.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
  ],
  testimonials: [
    {
      name: "Nancy L.",
      location: "Laconia, NH",
      rating: 5,
      text: "My cruise to the Caribbean was in 4 days and my passport was expired. Fast Passport Center got me a new one in 36 hours. Absolutely saved our family vacation.",
      service: "Emergency Passport",
    },
    {
      name: "Greg H.",
      location: "Gilford, NH",
      rating: 5,
      text: "Needed to renew for a business trip to Montreal. The process was smooth, they reviewed my documents, and I had my passport in 4 days. Highly recommend.",
      service: "Passport Renewal",
    },
    {
      name: "Linda S.",
      location: "Meredith, NH",
      rating: 5,
      text: "Lost my passport the week before my European trip. They walked me through the DS-64 process and got me a replacement in 2 days. Incredible service.",
      service: "Lost Passport",
    },
    {
      name: "Tom W.",
      location: "Belmont, NH",
      rating: 5,
      text: "As a frequent international traveler from the Lakes Region, I can't afford passport delays. Fast Passport Center has been my go-to for years. Professional and reliable.",
      service: "Expedited Renewal",
    },
  ],
  facilities: [
    {
      id: "laconia-1",
      name: "Laconia Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "76 N Main St",
      city: "Laconia",
      state: "NH",
      zip: "03246",
      phone: "(603) 524-0054",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.3 miles from downtown Laconia",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Primary acceptance facility for Laconia. Appointments required. Call ahead to confirm passport acceptance times.",
      icon: "ri-mail-line",
    },
    {
      id: "laconia-2",
      name: "Gilford Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "2 Country Club Rd",
      city: "Gilford",
      state: "NH",
      zip: "03249",
      phone: "(603) 524-9615",
      hours: "Mon–Fri: 9:00 AM – 3:00 PM",
      distance: "0.8 miles from downtown Gilford",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Gilford residents. Limited hours — book appointments well in advance.",
      icon: "ri-mail-line",
    },
    {
      id: "laconia-3",
      name: "Belmont Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "8 Main St",
      city: "Belmont",
      state: "NH",
      zip: "03220",
      phone: "(603) 267-8081",
      hours: "Mon–Fri: 9:00 AM – 3:00 PM",
      distance: "0.5 miles from downtown Belmont",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Popular for northern Belknap County residents. Appointment strongly recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "laconia-4",
      name: "Meredith Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1 Main St",
      city: "Meredith",
      state: "NH",
      zip: "03253",
      phone: "(603) 279-4401",
      hours: "Mon–Fri: 9:00 AM – 3:00 PM",
      distance: "0.4 miles from downtown Meredith",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for northern Lakes Region residents. Photo services available. Book ahead.",
      icon: "ri-mail-line",
    },
    {
      id: "laconia-5",
      name: "Plymouth Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1 Main St",
      city: "Plymouth",
      state: "NH",
      zip: "03264",
      phone: "(603) 536-3991",
      hours: "Mon–Fri: 9:00 AM – 3:00 PM",
      distance: "0.5 miles from downtown Plymouth",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves central New Hampshire near PSU. Limited passport hours — call to confirm.",
      icon: "ri-mail-line",
    },
    {
      id: "laconia-6",
      name: "Laconia City Clerk",
      type: "City Clerk Passport Acceptance",
      address: "45 Beacon St E",
      city: "Laconia",
      state: "NH",
      zip: "03246",
      phone: "(603) 527-1260",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "0.1 miles from City Hall",
      website: "https://www.laconianh.gov/",
      websiteLabel: "Laconia City Clerk",
      notes: "Major municipal facility with extended hours. Accepts walk-ins but appointments recommended for faster service.",
      icon: "ri-government-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — downtown Boston", fpc: "No — ship from home or office" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Laconia photo shops often use generic sizes. Passport photos must be exactly 2x2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 reasons for rejection at Belknap County acceptance facilities.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Fees change frequently. Many Laconia applicants show up with incorrect checks and have to reschedule their appointment.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation or travel itinerary, the Boston regional agency will turn you away for emergency appointments.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For minors, both parents must appear or submit notarized consent. This trips up many Laconia families at the clerk.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "Laconia travelers often assume standard processing will be fine. It takes 6–13 weeks. Start expedited at least 3 weeks before travel.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location in Laconia." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Laconia address — trackable every step of the way." },
  ],
  faqs: [
    { q: "Can I get an emergency passport in Laconia before international travel?", a: "Yes. Fast Passport Center provides emergency passport services in Laconia for travelers with urgent international flights. Our registered couriers hand-deliver your documents directly to U.S. Department of State facilities, achieving 24–48 hour turnaround for most Lakes Region travelers." },
    { q: "How fast can I renew my passport in Laconia?", a: "Fast Passport Center offers fast passport renewal in Laconia in as little as 24–48 hours through our emergency courier service. For less urgent travel, our 3–5 day expedited option provides the best value." },
    { q: "What if my passport expires before my cruise?", a: "If your passport expires before your cruise, contact Fast Passport Center immediately. We can expedite your passport renewal in Laconia in 24–48 hours so you don't miss your departure." },
    { q: "Can I get a same-day passport appointment near Laconia?", a: "Same-day passport appointments are only available at the Boston Regional Passport Agency for documented life-or-death emergencies. For all other urgent travel, Fast Passport Center's 24–48 hour emergency courier processing is the fastest guaranteed option for Laconia residents." },
    { q: "Can I expedite a child passport in Laconia?", a: "Yes. Fast Passport Center expedites child passports in Laconia for minors under 16. Both parents must appear or submit notarized consent, along with the child's birth certificate and both parents' IDs." },
    { q: "What should I do if I lost my passport before a trip?", a: "If you lost your passport before a trip, contact Fast Passport Center immediately. We provide lost passport replacement services in Laconia with DS-64 form guidance. Our emergency courier service can get your replacement passport in 24–48 hours." },
    { q: "How fast can I get a passport in Laconia?", a: "With Fast Passport Center's emergency courier service, Laconia travelers can receive their passport in as little as 24–48 hours. Our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities, bypassing standard mail delays." },
    { q: "Do I need to visit the Boston Passport Agency?", a: "No. With Fast Passport Center, you complete everything online and ship your documents overnight. Our couriers handle the in-person delivery to State Department facilities. You only need to visit a local acceptance clerk if you're a first-time applicant or need a name change." },
    { q: "Where are the closest passport acceptance facilities in Laconia?", a: "Popular options include the Laconia Post Office (03246), Gilford Post Office (03249), Belmont Post Office (03220), Meredith Post Office (03253), and Laconia City Clerk (03246). All require appointments. We help you find the most convenient location and schedule your visit." },
    { q: "Can I get a same-day passport in Laconia?", a: "Same-day passports are only available at the Boston Regional Passport Agency for documented life-or-death emergencies with proof of travel within 72 hours. For all other urgent travel, Fast Passport Center's 24–48 hour emergency processing is the fastest reliable option." },
    { q: "What if I live in a nearby town like Gilford or Meredith?", a: "We serve all of the Lakes Region including Gilford, Belmont, Meredith, Alton, Center Harbor, Tilton, Franklin, Wolfeboro, Tuftonboro, Moultonborough, Ossipee, Plymouth, Ashland, and Holderness. Our overnight shipping labels work from any UPS location, so your location doesn't affect processing speed." },
    { q: "How much does expedited passport service cost in Laconia?", a: "Government fees are fixed: $130 for a passport book (adult) plus a $60 expedite fee. Fast Passport Center's service fees vary by processing speed — emergency 24–48 hour processing, 3–5 day expedited, or standard options. Contact us for a personalized quote based on your timeline." },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Laconia", address: "76 N Main St, Laconia, NH 03246", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — Laconia", address: "135 Union Ave, Laconia, NH 03246", hours: "Mon–Sun: 7AM–11PM" },
    { name: "FedEx Office — Laconia", address: "1 S Main St, Laconia, NH 03246", hours: "Mon–Fri: 7AM–9PM, Sat: 8AM–6PM" },
    { name: "UPS Store — Laconia", address: "10 Veterans Sq, Laconia, NH 03246", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–5PM" },
    { name: "Walmart — Laconia", address: "1415 Lake Shore Rd, Laconia, NH 03246", hours: "Mon–Sun: 6AM–11PM" },
    { name: "Rite Aid — Gilford", address: "2 Country Club Rd, Gilford, NH 03249", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Postal Annex — Meredith", address: "1 Main St, Meredith, NH 03253", hours: "Mon–Fri: 9AM–6PM, Sat: 9AM–4PM" },
    { name: "Costco — Tilton", address: "8 N Main St, Tilton, NH 03276", hours: "Mon–Fri: 10AM–8:30PM, Sat–Sun: 9:30AM–6PM" },
  ],
  dmvLocations: [
    { name: "DMV — Laconia", address: "10 Veterans Sq, Laconia, NH 03246", hours: "Mon–Fri: 8AM–5PM", note: "REAL ID and driver's license renewals. Appointment required.", featured: true },
    { name: "DMV — Tilton", address: "11 E Main St, Tilton, NH 03276", hours: "Mon–Fri: 8AM–5PM", note: "Passport-ready photo services available. Online appointment booking.", featured: false },
    { name: "DMV — Plymouth", address: "1 Main St, Plymouth, NH 03264", hours: "Mon–Fri: 8AM–5PM", note: "REAL ID processing. Walk-ins limited — book online first.", featured: false },
    { name: "DMV — Franklin", address: "2 Main St, Franklin, NH 03235", hours: "Mon–Fri: 8AM–5PM", note: "Full-service center for central New Hampshire residents.", featured: true },
  ],
  airports: [
    { code: "LCI", name: "Laconia Municipal Airport", distance: "3 miles from downtown Laconia", address: "65 Aviation Dr, Laconia, NH 03246", note: "Regional airport for general aviation and seasonal commercial flights. Convenient for Lakes Region travelers.", flightUrl: "https://www.laconiaairport.org/", flightLabel: "Laconia Airport", featured: true },
    { code: "MHT", name: "Manchester-Boston Regional Airport", distance: "45 miles from Laconia", address: "1 Airport Rd, Manchester, NH 03103", note: "Closest major commercial airport for Laconia travelers. International flights to Canada and Europe. Major domestic hub.", flightUrl: "https://www.flymanchester.com/", flightLabel: "MHT Flight Info", featured: false },
    { code: "BOS", name: "Boston Logan International Airport", distance: "90 miles from Laconia", address: "1 Harborside Dr, Boston, MA 02128", note: "Major international hub for long-haul flights to Europe, Asia, and South America. Accessible via I-93.", flightUrl: "https://www.massport.com/logan-airport", flightLabel: "Logan Airport", featured: false },
  ],
  vitalRecordsIcon: "ri-heart-pulse-line",
  vitalRecordsLinks: [
    { label: "Birth & Death Certificates (NH Vital Records)", url: "https://www.sos.nh.gov/vital-records", desc: "New Hampshire Division of Vital Records Administration for birth, death, marriage, and divorce certificates statewide" },
    { label: "Marriage Licenses (Belknap County Clerk)", url: "https://www.belknapcounty.gov/", desc: "Belknap County Clerk for Laconia and Belknap County" },
    { label: "Divorce Decrees (Belknap County Superior Court)", url: "https://www.courts.nh.gov/our-courts/superior-court", desc: "New Hampshire Superior Court for Belknap County" },
    { label: "VitalChek Online Ordering", url: "https://www.vitalchek.com/", desc: "Order New Hampshire vital records online via VitalChek" },
  ],
};